import { useCallback, useMemo, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  Button,
  Checkbox,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip,
  Box
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Add, Delete, Edit, LocationCity } from '@mui/icons-material';
import Title from '../../components/Title';
import {
  deleteCityAsync,
  getCitiesAsync,
  selectCities
} from '../../redux/slices/citiesSlice';
import CitiesDialog from './CitiesDialog';
import { Props } from './interface';
import { selectAdmins, setCurrentCity } from '../../redux/slices/adminsSlice';

const PREFIX = 'CitiesPage';

const classes = {
  header: `${PREFIX}-header`
};

const Root = styled('div')({
  [`& .${classes.header}`]: {
    marginBottom: 20
  }
});

const CitiesPage = (props: Props) => {
  const { isSuperAdmin } = props;
  const theme = useTheme();

  const citiesStore = useSelector(selectCities);
  const adminInfo = useSelector(selectAdmins);

  const dispatch = useDispatch();

  const cities = useMemo(
    () => (isSuperAdmin ? citiesStore.items : adminInfo.info?.cities || []),
    [isSuperAdmin, citiesStore.items, adminInfo.info]
  );

  const [dialogState, setDialogState] = useState<{
    open: boolean;
    cityId?: string;
  }>({ open: false });

  const openDialog = useCallback(
    (cityId?: string) => () => setDialogState({ open: true, cityId }),
    []
  );

  const closeDialog = useCallback(() => setDialogState({ open: false }), []);

  const deleteCity = useCallback(
    (cityId: string) => async () => {
      // eslint-disable-next-line no-restricted-globals
      const confirmation = confirm("Ви впевнені що хочете видалити місто?");
      if (confirmation) {
        dispatch(deleteCityAsync(cityId));
        dispatch(getCitiesAsync());
      }
    },
    [dispatch]
  );

  const createSetCityHandler = useCallback(
    cityId => () => {
      if (isSuperAdmin && cityId === adminInfo.currentCity) {
        dispatch(setCurrentCity(undefined));
      } else {
        dispatch(setCurrentCity(cityId));
      }
    },
    [adminInfo.currentCity, dispatch, isSuperAdmin]
  );

  return (
    <Root>
      <Grid
        container
        alignItems='center'
        className={classes.header}
        spacing={4}
      >
        <Grid item>
          <Title>Міста</Title>
        </Grid>
        {isSuperAdmin && (
          <Grid item>
            <Button
              color='primary'
              startIcon={<Add />}
              variant='contained'
              onClick={openDialog()}
            >
              Додати
            </Button>
          </Grid>
        )}
      </Grid>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Вибір міста</TableCell>
              <TableCell>Назва</TableCell>
              <TableCell align='right'>Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cities.map(city => (
              <TableRow key={city._id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      checked={adminInfo.currentCity === city._id}
                      color='primary'
                      onChange={createSetCityHandler(city._id)}
                    />
                    <Tooltip title={city.cityName} arrow>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: 'rgba(33, 150, 243, 0.12)',
                          borderRadius: '8px'
                        }}
                      >
                        <LocationCity sx={{ fontSize: 24, color: '#2196F3' }} />
                      </Avatar>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell>{city.cityName}</TableCell>
                <TableCell align='right'>
                  {
                    isSuperAdmin ? (
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title='Редагувати'>
                          <IconButton
                            size='medium'
                            onClick={openDialog(city._id)}
                            sx={{
                              bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                              color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                              borderRadius: '8px',
                              '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
                            }}
                          >
                            <Edit fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Видалити'>
                          <IconButton
                            size='medium'
                            onClick={deleteCity(city._id)}
                            sx={{
                              bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                              color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                              borderRadius: '8px',
                              '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
                            }}
                          >
                            <Delete fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : null
                  }
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CitiesDialog {...dialogState} onClose={closeDialog} />
    </Root>
  );
};

export default CitiesPage;
